import { Matrix4, Object3D, Vector3 } from 'three'

export const computeCentroid = (objects: Object3D[]): Vector3 => {
  const centroid = new Vector3()

  objects.forEach((object) => {
    const position = new Vector3()
    object.getWorldPosition(position)
    centroid.add(position)
  })

  return centroid.divideScalar(objects.length || 1)
}

export const createSelectionPivot = (objects: Object3D[]): Object3D => {
  const pivot = new Object3D()
  pivot.position.copy(computeCentroid(objects))
  pivot.updateMatrixWorld(true)
  return pivot
}

export type PivotTransformSnapshot = {
  pivotMatrixWorldInverse: Matrix4
  objectMatricesWorld: Map<string, Matrix4>
}

export const snapshotPivotTransform = (pivot: Object3D, objects: Object3D[]): PivotTransformSnapshot => {
  pivot.updateMatrixWorld(true)

  const objectMatricesWorld = new Map<string, Matrix4>()
  objects.forEach((object) => {
    object.updateMatrixWorld(true)
    objectMatricesWorld.set(object.uuid, object.matrixWorld.clone())
  })

  return {
    pivotMatrixWorldInverse: pivot.matrixWorld.clone().invert(),
    objectMatricesWorld,
  }
}

// Applies the pivot's world-space delta (since `snapshot` was taken) to every
// object, so a single gizmo drag moves/rotates the whole selection as one
// rigid group instead of just the pivot itself.
export const applyPivotDelta = (pivot: Object3D, snapshot: PivotTransformSnapshot, objects: Object3D[]) => {
  pivot.updateMatrixWorld(true)
  const delta = pivot.matrixWorld.clone().multiply(snapshot.pivotMatrixWorldInverse)

  objects.forEach((object) => {
    const startWorldMatrix = snapshot.objectMatricesWorld.get(object.uuid)
    if (!startWorldMatrix || !object.parent) return

    const newWorldMatrix = delta.clone().multiply(startWorldMatrix)

    object.parent.updateMatrixWorld(true)
    const newLocalMatrix = object.parent.matrixWorld.clone().invert().multiply(newWorldMatrix)

    newLocalMatrix.decompose(object.position, object.quaternion, object.scale)
    object.updateMatrix()
  })
}
